import { useAuthStore } from './authStore'
import type { CatalogSub } from '../utils/libraryAddress'

/**
 * ADR-106 P4 — each of the six swipe-chain stops remembers its own state and
 * scroll position for the session, and gets it back however the learner
 * returns (tap, device back, coming back from a deck, later a swipe).
 *
 * What each stop keeps is ADR-106's table (first owner-decisions block, as
 * completed by Q11). Deliberately NOT here: Mine's select mode and inline
 * rename, and every menu or sheet — leaving a stop ends them.
 *
 * Plain module state, never storage: a reload or a new tab starts clean. It is
 * cleared whenever the signed-in user changes (logout, or a different
 * account), so one person never sees another's search text, filters or place
 * in a list on a shared device.
 *
 * The URL still names only the stop (`?seg=`, `?sub=`, ADR-106 P1); none of
 * this goes into it.
 */

export type StopKey =
  | 'home'
  | 'mine'
  | 'catalog-decks'
  | 'catalog-courses'
  | 'catalog-explore'
  | 'progress'

export interface MineMemory {
  folderId: string | null
  sort: string
  page: number
  /** What is typed in the search field. */
  searchInput: string
  /** The search actually applied (Mine applies on submit). */
  search: string
}

export interface CatalogListMemory {
  level: string | null
  page: number
}

export interface CatalogCoursesMemory extends CatalogListMemory {
  language: string
}

export interface ExploreMemory {
  searchInput: string
  /** The search actually applied (after Explore's debounce). */
  search: string
  /** How many "load more" pages were loaded — the scroll offset needs them. */
  pageCount: number
}

interface Memory {
  scroll: Partial<Record<StopKey, number>>
  mine?: MineMemory
  catalogDecks?: CatalogListMemory
  catalogCourses?: CatalogCoursesMemory
  catalogExplore?: ExploreMemory
  /** The Catalog segment returns to the sub-tab it last showed (ADR table). */
  catalogSub?: CatalogSub
}

let memory: Memory = { scroll: {} }

/** A stop's state the first time it is shown in a session — the screen's own defaults. */
export const STOP_DEFAULTS = {
  mine: { folderId: null, sort: 'newest', page: 1, searchInput: '', search: '' } as MineMemory,
  catalogDecks: { level: null, page: 1 } as CatalogListMemory,
  catalogCourses: { level: null, page: 1, language: 'en' } as CatalogCoursesMemory,
  catalogExplore: { searchInput: '', search: '', pageCount: 1 } as ExploreMemory,
}

type ListKey = keyof typeof STOP_DEFAULTS

export function readStopMemory(): Readonly<Memory> {
  return memory
}

/** Whether this stop has been shown before in this session (so it has something to restore). */
export function hasStopMemory(key: ListKey): boolean {
  return memory[key] !== undefined
}

/** The stop's remembered state, or its defaults on a first visit. */
export function stopState<K extends ListKey>(key: K): (typeof STOP_DEFAULTS)[K] {
  return (memory[key] ?? STOP_DEFAULTS[key]) as (typeof STOP_DEFAULTS)[K]
}

/**
 * Merges into a stop's remembered state. Two components can own parts of one
 * stop (Mine's search lives a level above its folder/sort/page), so writes
 * merge rather than replace.
 */
export function patchStopState<K extends ListKey>(key: K, patch: Partial<(typeof STOP_DEFAULTS)[K]>): void {
  memory = { ...memory, [key]: { ...stopState(key), ...patch } }
}

export function writeCatalogSub(sub: CatalogSub): void {
  memory = { ...memory, catalogSub: sub }
}

export function readStopScroll(stop: StopKey): number | undefined {
  return memory.scroll[stop]
}

export function writeStopScroll(stop: StopKey, top: number): void {
  memory.scroll = { ...memory.scroll, [stop]: top }
}

export function clearStopMemory(): void {
  memory = { scroll: {} }
}

// A different signed-in person (logout sets it to nobody) starts with nothing
// remembered. A profile update for the same person keeps the memory.
useAuthStore.subscribe((state, prev) => {
  if ((state.user?.id ?? null) !== (prev.user?.id ?? null)) clearStopMemory()
})
